import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FileTextOutlined,
  AudioOutlined,
  ThunderboltOutlined,
  PhoneOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { Button, Spin } from "antd";
import { useTranslation } from "react-i18next";

import { fetchTemplates, fetchRules, fetchSettings } from "./service";
import { resolveSetupTestNavigation } from "./setupRouting";
import styles from "./style.module.scss";

const SETUP_TEST_KEY = "sonic_setup_test_complete";

export const markSetupTestComplete = () => {
  try {
    localStorage.setItem(SETUP_TEST_KEY, "1");
  } catch (e) {
    // ignore storage errors
  }
};

export const isSetupTestComplete = () => {
  try {
    return localStorage.getItem(SETUP_TEST_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const SetupTab = ({ active, onNavigate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [cachedTemplates, setCachedTemplates] = useState([]);
  const [cachedRules, setCachedRules] = useState([]);
  const [steps, setSteps] = useState({
    template: false,
    voice: false,
    rule: false,
    test: false,
  });
  const redirected = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [tplRes, rulesRes, settingsRes] = await Promise.all([
      fetchTemplates(),
      fetchRules(),
      fetchSettings(),
    ]);

    const templates = tplRes && !tplRes.error ? tplRes.templates || [] : [];
    const rules = rulesRes && !rulesRes.error ? rulesRes.rules || [] : [];
    const vp =
      settingsRes && !settingsRes.error && settingsRes.settings
        ? settingsRes.settings.voice_provider_config || {}
        : {};
    const greeting = (vp.greeting || "").trim();
    const placeholder = t("sonic.greeting_placeholder").trim();

    setCachedTemplates(templates);
    setCachedRules(rules);
    setSteps({
      template: templates.length > 0,
      voice: greeting.length > 0 && greeting !== placeholder,
      rule: rules.length > 0,
      test: isSetupTestComplete(),
    });
    setLoading(false);
  }, [t]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const allComplete = steps.template && steps.voice && steps.rule && steps.test;

  useEffect(() => {
    if (!active || !allComplete || redirected.current) return undefined;
    redirected.current = true;
    const timer = setTimeout(() => {
      onNavigate("conversations");
    }, 2000);
    return () => clearTimeout(timer);
  }, [active, allComplete, onNavigate]);

  if (loading) {
    return (
      <div className={styles.centeredSpin}>
        <Spin />
      </div>
    );
  }

  if (allComplete) {
    return (
      <div className={styles.tabInner}>
        <div className={`${styles.sonicPanel} ${styles.setupPanel}`}>
          <div className={styles.setupSuccess}>
            <CheckOutlined className={styles.setupSuccessIcon} />
            <h2>{t("sonic.setup_success_title")}</h2>
            <p>{t("sonic.setup_success_subtitle")}</p>
          </div>
        </div>
      </div>
    );
  }

  const stepDefs = [
    {
      key: "template",
      icon: <FileTextOutlined />,
      title: t("sonic.setup_step1_title"),
      desc: t("sonic.setup_step1_desc"),
      cta: t("sonic.setup_step1_cta"),
      done: steps.template,
      action: () => onNavigate("templates"),
    },
    {
      key: "voice",
      icon: <AudioOutlined />,
      title: t("sonic.setup_step2_title"),
      desc: t("sonic.setup_step2_desc"),
      cta: t("sonic.setup_step2_cta"),
      done: steps.voice,
      action: () => onNavigate("voice"),
    },
    {
      key: "rule",
      icon: <ThunderboltOutlined />,
      title: t("sonic.setup_step3_title"),
      desc: t("sonic.setup_step3_desc"),
      cta: t("sonic.setup_step3_cta"),
      done: steps.rule,
      action: () => onNavigate("rules", { create: true }),
    },
    {
      key: "test",
      icon: <PhoneOutlined />,
      title: t("sonic.setup_step4_title"),
      desc: t("sonic.setup_step4_desc"),
      cta: t("sonic.setup_step4_cta"),
      done: steps.test,
      action: () => {
        const dest = resolveSetupTestNavigation(cachedTemplates, cachedRules);
        onNavigate(dest.tab, { focus: dest.focus });
      },
    },
  ];

  return (
    <div className={styles.tabInner}>
      <div className={`${styles.sonicPanel} ${styles.setupPanel}`}>
        <header className={styles.setupHeader}>
          <h2>{t("sonic.setup_title")}</h2>
          <p>{t("sonic.setup_subtitle")}</p>
        </header>

        <div className={styles.setupStepper}>
          {stepDefs.map((step, idx) => (
            <div
              key={step.key}
              className={[
                styles.setupStep,
                step.done ? styles.setupStepDone : "",
              ].join(" ")}
            >
              <div className={styles.setupStepRail}>
                <div
                  className={[
                    styles.setupStepIcon,
                    step.done ? styles.setupStepIconDone : "",
                  ].join(" ")}
                >
                  {step.done ? <CheckOutlined /> : step.icon}
                </div>
                {idx < stepDefs.length - 1 && (
                  <div
                    className={[
                      styles.setupConnector,
                      step.done ? styles.setupConnectorDone : "",
                    ].join(" ")}
                  />
                )}
              </div>
              <div className={styles.setupStepContent}>
                <div className={styles.setupStepTop}>
                  <span className={styles.setupStepNum}>{idx + 1}</span>
                  <h3>{step.title}</h3>
                  <span
                    className={
                      step.done
                        ? styles.setupStepBadge
                        : styles.setupStepBadgePending
                    }
                  >
                    {step.done
                      ? t("sonic.setup_complete")
                      : t("sonic.setup_incomplete")}
                  </span>
                </div>
                <p>{step.desc}</p>
                {!step.done && (
                  <Button type="primary" size="small" onClick={step.action}>
                    {step.cta}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SetupTab;
